<template>
  <v-container class="patient-information-form-wrapper">
    <div v-if="preloader">
      <SubmissionPageSkeleton />
    </div>
    <div v-if="isReopening" class="mb-4">
      <v-chip :color="isReopenEligible ? 'success' : 'warning'" variant="tonal" size="large" block class="pa-4">
        <template v-if="isReopenEligible"> ✓ Ready to re-open submission </template>
        <template v-else>
          <span v-if="orderInfo.status === 'Response received'">
            ⚠ In order to re-open a submission you need to update one or more of the following sections: Patient,
            Provider or Prescription Information.
          </span>
          <span v-if="orderInfo.status === 'Payor/Medication not supported'">
            ⚠ In order to re-open a submission you need to update either of the following fields: RxBIN, RxPCN, RxGroup,
            National Drug Code or Medication.
          </span>
          <span v-if="orderInfo.status === 'Requires more information'">
            ⚠ Review the error note, update the submission (e.g. remove or replace supporting documents), then re-open
            to resubmit.
          </span>
        </template>
      </v-chip>
    </div>

    <v-expansion-panels v-if="!preloader" v-model="expandedSections" multiple>
      <v-expansion-panel v-if="showAppeal" id="draft-appeal-section" value="appeal" data-section="appeal">
        <v-expansion-panel-title class="text-primary weight-600">
          <v-icon icon="mdi-file-document-edit-outline" class="mr-2"></v-icon>
          Draft an appeal
        </v-expansion-panel-title>
        <v-expansion-panel-text>
          <AppealLetterSection :order="orderInfo" @appeal-sent="(e) => emit('appealSent', e)" />
        </v-expansion-panel-text>
      </v-expansion-panel>
      <v-expansion-panel
        v-if="
          orderInfo &&
          orderInfo.portalForms &&
          orderInfo.portalForms?.length &&
          orderInfo.submissionMethod === 'CoverMyMeds'
        "
        value="portal-forms">
        <v-expansion-panel-title class="text-primary weight-600">
          <v-icon icon="mdi-checkbox-marked" class="mr-2"></v-icon>
          {{ orderInfo.portalSubmissionForm }}
        </v-expansion-panel-title>
        <v-expansion-panel-text>
          <PortalFormSelection
            v-if="!preloader && props.actionsVisible"
            :order-info="orderInfo"
            @change-form-selection="changeFormSelection" />
          <div v-else-if="!preloader" class="text-body-2">{{ orderInfo.portalSubmissionForm || "—" }}</div>
        </v-expansion-panel-text>
      </v-expansion-panel>

      <v-expansion-panel
        v-if="
          orderInfo &&
          orderInfo.portalSubmissionForm &&
          orderInfo.submissionMethod === 'CoverMyMeds' &&
          !orderInfo.portalForms?.length
        "
        value="portal-forms">
        <v-expansion-panel-title class="text-primary weight-600">
          <v-icon icon="mdi-checkbox-marked" class="mr-2"></v-icon>
          {{ orderInfo.portalSubmissionForm }}
        </v-expansion-panel-title>
      </v-expansion-panel>

      <v-expansion-panel value="patient-info" data-section="patient-info">
        <v-expansion-panel-title class="text-primary weight-600">
          <v-icon icon="mdi-account" class="mr-2"></v-icon>
          Patient Information
        </v-expansion-panel-title>
        <v-expansion-panel-text>
          <PatientInformationFormPreview
            v-if="!preloader"
            :order-info="orderInfo"
            :is-editable="orderFieldsEditable"
            :form-button-clicked="formButtonClicked && orderFieldsEditable"
            @update-order-info-field="updateOrderInfoField" />
        </v-expansion-panel-text>
      </v-expansion-panel>

      <!-- Provider Information Section -->
      <v-expansion-panel value="provider-info" data-section="provider-info">
        <v-expansion-panel-title class="text-primary weight-600">
          <v-icon icon="mdi-doctor" class="mr-2"></v-icon>
          Provider Information
        </v-expansion-panel-title>
        <v-expansion-panel-text>
          <ProviderInformationFormPreview
            v-if="!preloader"
            :order-info="orderInfo"
            :is-editable="orderFieldsEditable"
            :form-button-clicked="formButtonClicked && orderFieldsEditable"
            @update-order-info-field="updateOrderInfoField" />
        </v-expansion-panel-text>
      </v-expansion-panel>

      <!-- Medical Information Section -->
      <v-expansion-panel value="medical-info" data-section="medical-info">
        <v-expansion-panel-title class="text-primary weight-600">
          <v-icon icon="mdi-medication" class="mr-2"></v-icon>
          Prescription Information
        </v-expansion-panel-title>
        <v-expansion-panel-text>
          <MedicalInformationForm
            v-if="!preloader"
            :order-info="orderInfo"
            :is-editable="orderFieldsEditable"
            :form-button-clicked="formButtonClicked && orderFieldsEditable"
            :allow-change-formulary="true"
            @update-order-info-field="updateOrderInfoField"
            @add-extra-medication="addExtraMedication"
            @remove-extra-medication="removeExtraMedication"
            @remove-primary-medication="removePrimaryMedication"
            @update-extra-medication="updateExtraMedicationField"
            @change-formulary="changeFormulary" />
        </v-expansion-panel-text>
      </v-expansion-panel>

      <!-- Patient Diagnosis Information Section -->
      <v-expansion-panel value="diagnosis-info" data-section="diagnosis-info">
        <v-expansion-panel-title class="text-primary weight-600">
          <v-icon icon="mdi-account-heart" class="mr-2"></v-icon>
          Patient Diagnosis Information
        </v-expansion-panel-title>
        <v-expansion-panel-text>
          <v-row class="patient-information-container">
            <v-col cols="12">
              <v-row class="patient-information">
                <v-col cols="12" class="header-with-select-box">
                  <DiagnosisSelectionPreviewComponent
                    :medication="orderInfo.medication"
                    :order-key="orderInfo.orderKey"
                    :primary-diagnosis-name="orderInfo.primaryDiagnosisName"
                    :primary-diagnosis-icd10-code="orderInfo.primaryDiagnosisIcd10Code"
                    :secondary-diagnosis="orderInfo.secondaryDiagnosis"
                    :is-editable="orderFieldsEditable"
                    :form-button-clicked="formButtonClicked && orderFieldsEditable"
                    :llm-prediction="orderInfo.diagnosisLlmScores"
                    @update-primary-diagnosis="updatePrimaryDiagnosis"
                    @update-other-diagnosis="updateOtherDiagnosis"
                    @add-other-diagnosis="addOtherDiagnosis" />
                </v-col>
              </v-row>
            </v-col>
          </v-row>
        </v-expansion-panel-text>
      </v-expansion-panel>

      <!-- Patient Outcome Notification Section -->
      <v-expansion-panel
        v-if="orderInfo.submissionMethod === 'CoverMyMeds'"
        value="patient-notification"
        data-section="patient-notification">
        <v-expansion-panel-title class="text-primary weight-600">
          <v-icon icon="mdi-bell-ring" class="mr-2"></v-icon>
          Patient Outcome Notifications
        </v-expansion-panel-title>
        <v-expansion-panel-text>
          <PatientNotificationComponent
            :order-info="orderInfo"
            :patient-phone="orderInfo.patientPhone"
            :is-editable="orderFieldsEditable"
            @update-order-info-field="updateOrderInfoField" />
        </v-expansion-panel-text>
      </v-expansion-panel>

      <!-- Additional Questions Section -->
      <v-expansion-panel
        v-if="orderInfo && orderInfo.basicPlusQuestions && orderInfo.basicPlusQuestions.length > 0"
        value="additional-questions"
        data-section="additional-questions">
        <v-expansion-panel-title class="text-primary weight-600">
          <v-icon icon="mdi-help-circle" class="mr-2"></v-icon>
          {{ basicPlusQuestionsTitle }}
        </v-expansion-panel-title>
        <v-expansion-panel-text>
          <QuestionsSelectionComponent
            :order-key="orderInfo.orderKey"
            :title="basicPlusQuestionsTitle"
            :is-editable="orderFieldsEditable"
            :payer-questions="orderInfo.basicPlusQuestions"
            :checklist="orderInfo.basicPlusChecklist"
            :llm-prediction="orderInfo.basicPlusChecklistLlmScores"
            :form-button-clicked="formButtonClicked && orderFieldsEditable"
            :submission-method="orderInfo.submissionMethod"
            @update-checklist="updateBasicPlusChecklistSelection"
            @has-live-validation-errors="(v) => onLiveValidationErrors(v, 'basicPlus')" />
        </v-expansion-panel-text>
      </v-expansion-panel>

      <!-- Insurance Coverage Criteria Section -->
      <v-expansion-panel
        v-if="
          orderInfo &&
          orderInfo.payerQuestions &&
          orderInfo.payerQuestions.length > 0 &&
          !orderInfo.editableSubmissionForm
        "
        value="insurance-coverage"
        data-section="insurance-coverage">
        <v-expansion-panel-title class="text-primary weight-600">
          <v-icon icon="mdi-shield-check" class="mr-2"></v-icon>
          {{ payerQuestionsTitle }}
        </v-expansion-panel-title>
        <v-expansion-panel-text>
          <QuestionsSelectionComponent
            :order-key="orderInfo.orderKey"
            :title="payerQuestionsTitle"
            :payer-questions="orderInfo.payerQuestions"
            :portal-payer-questions-json="orderInfo.portalPayerQuestionsJson"
            :is-editable="clinicalAnswersEditable"
            :checklist="orderInfo.checklist"
            :llm-prediction="orderInfo.checklistLlmScores"
            :policy-reviews="orderInfo.checklistPolicyReviews"
            :form-button-clicked="formButtonClicked && clinicalAnswersEditable"
            :submission-method="orderInfo.submissionMethod"
            @update-checklist="updateChecklistSelection"
            @has-live-validation-errors="(v) => onLiveValidationErrors(v, 'checklist')"
            @clear-policy-review="onClearPolicyReview" />
        </v-expansion-panel-text>
      </v-expansion-panel>

      <!-- Editable PDF Section -->
      <v-expansion-panel
        v-if="orderInfo && orderInfo.editableSubmissionForm"
        value="editable-pdf"
        data-section="editable-pdf">
        <v-expansion-panel-title class="text-primary weight-600">
          <v-icon icon="mdi-file-pdf-box" class="mr-2"></v-icon>
          Submission Form
        </v-expansion-panel-title>
        <v-expansion-panel-text>
          <EditablePdfComponent
            v-if="!props.disabled || !props.filledFormPdfBlob"
            ref="editablePdf"
            :form-button-clicked="formButtonClicked && !props.disabled"
            :pdf-form="orderInfo.editableSubmissionForm"
            :disabled="props.disabled" />
          <FlattenedPdfComponent v-else :pdf-form="props.filledFormPdfBlob" />
        </v-expansion-panel-text>
      </v-expansion-panel>

      <!-- Supporting Documents Section -->
      <v-expansion-panel value="supporting-docs" data-section="supporting-docs">
        <v-expansion-panel-title class="text-primary weight-600">
          <v-icon icon="mdi-file-document" class="mr-2"></v-icon>
          Supporting Documents
        </v-expansion-panel-title>
        <v-expansion-panel-text>
          <v-row v-if="docsNotRequiredOptinEnabled">
            <v-col cols="12">
              <v-checkbox
                id="docs-not-required-opt-in"
                v-model="orderInfo.patientChartNotNeededOptIn"
                :disabled="!orderFieldsEditable"
                color="accent"
                hide-details
                label="This payor does not require supporting documents, skip validation"
                @change="updateOrderInfoField('patientChartNotNeededOptIn', orderInfo.patientChartNotNeededOptIn)" />
            </v-col>
          </v-row>
          <SupportingDocumentsPreviewComponent
            :order-key="orderInfo.orderKey"
            :medical-necessity="orderInfo.medicalNecessity"
            :medical-necessity-letter="orderInfo.medicalNecessityLetter"
            :benefit-type="orderInfo.benefitType"
            :patient-chart="orderInfo.patientChart"
            :medical-necessity-template="orderInfo.medicalNecessityTemplate"
            :supplemental-docs="supplementalDocs"
            :form-button-clicked="formButtonClicked && supportingDocsEditable"
            :patient-chart-not-needed="orderInfo.patientChartNotNeededOptIn === true"
            :is-editable="supportingDocsEditable"
            @add-uploaded-file="updateSupportingDocs"
            @delete-uploaded-file="updateSupportingDocs"
            @add-free-form-supplemental-docs="addFreeFormSupplementalDocs" />
        </v-expansion-panel-text>
      </v-expansion-panel>

      <!-- Expedited Review Section -->
      <v-expansion-panel value="expedited-review">
        <v-expansion-panel-title class="text-primary weight-600">
          <v-icon icon="mdi-clock-fast" class="mr-2"></v-icon>
          Expedited Review
        </v-expansion-panel-title>
        <v-expansion-panel-text>
          <ExpeditedReviewComponent
            :expedited-review-within24h="orderInfo.expeditedReviewWithin24h"
            :expedited-review-statement="orderInfo.expeditedReviewStatement"
            :is-editable="orderFieldsEditable"
            @update-order-info-field="updateOrderInfoField" />
        </v-expansion-panel-text>
      </v-expansion-panel>

      <!-- Signature Section -->
      <v-expansion-panel id="signature-section" value="signature">
        <v-expansion-panel-title class="text-primary weight-600">
          <v-icon icon="mdi-pen" class="mr-2"></v-icon>
          Signature
        </v-expansion-panel-title>
        <v-expansion-panel-text>
          <SignatureInputComponent
            v-model:signature-image="userSignature"
            v-model:signature-strokes="userSignatureStrokes"
            :order-key="orderInfo.orderKey"
            :signature="orderInfo.signature"
            :form-button-clicked="formButtonClicked && !props.disabled"
            :disabled="props.disabled"
            :electronic-signature-enabled="electronicSignatureEnabled"
            @update-signature="updateSignature" />
        </v-expansion-panel-text>
      </v-expansion-panel>
    </v-expansion-panels>
    <v-row v-if="!preloader && !isReadOnlyDevice && props.actionsVisible" class="mt-4 justify-end">
      <v-col v-if="props.archivable && orderInfo?.orderKey" cols="auto">
        <v-btn v-if="isArchived" variant="outlined" @click="emit('unarchive')">Unarchive</v-btn>
        <v-btn v-else variant="outlined" @click="emit('archive')">Archive</v-btn>
      </v-col>
      <v-col cols="auto">
        <v-tooltip
          :text="sendToPrescriberDisabledReason"
          location="top"
          :disabled="!isSendToPrescriberDisabledByCmmKey">
          <template #activator="{ props: tooltipProps }">
            <span v-bind="tooltipProps" class="d-inline-block">
              <v-btn
                variant="outlined"
                :disabled="isSendToPrescriberDisabled"
                :loading="draftSavingInProgress"
                @click="sendToPrescriber">
                Send to Prescriber
              </v-btn>
            </span>
          </template>
        </v-tooltip>
      </v-col>
      <v-col cols="auto">
        <v-btn
          variant="outlined"
          :disabled="draftSavingInProgress || props.disabled"
          :loading="draftSavingInProgress"
          @click="handleSavePartialOrderInfo">
          Save Draft
        </v-btn>
      </v-col>
      <v-col cols="auto">
        <v-tooltip :text="sendToPlanDisabledReason" location="top" :disabled="!isSendToPlanDisabledByDelegateAgreement">
          <template #activator="{ props: tooltipProps }">
            <span v-bind="tooltipProps" class="d-inline-block">
              <v-btn :disabled="submitButtonDisabled" @click="submitOrderInfo">
                {{ buttonText }}
              </v-btn>
            </span>
          </template>
        </v-tooltip>
      </v-col>
    </v-row>
    <span v-if="errors.length > 0" class="input-error-notification">
      <span v-for="error in errors" :key="error">{{ error }} </span>
    </span>

    <!-- Delegate agreement fuzzy match confirmation -->
    <v-dialog v-model="showDelegateAgreementDialog" max-width="520" persistent>
      <v-card>
        <v-card-title class="d-flex align-center">
          <v-icon color="warning" class="mr-2">mdi-account-check</v-icon>
          Confirm provider delegate agreement
        </v-card-title>
        <v-card-text>
          <p class="text-body-1 mb-2">
            No exact delegate agreement was found for
            <strong>{{ enteredProviderDisplayName }}</strong> (NPI {{ orderInfo?.providerNpiNumber }}).
          </p>
          <p class="text-body-1 mb-2">
            We found a delegate agreement for
            <strong>{{ suggestedProviderDisplayName }}</strong> with the same NPI. Is this the same provider?
          </p>
          <p class="text-body-2 text-medium-emphasis">
            If you confirm, the provider name on this order will be updated to match the agreement before submit.
          </p>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn variant="outlined" @click="showDelegateAgreementDialog = false">Cancel</v-btn>
          <v-btn color="primary" @click="confirmDelegateAgreementMatch">Yes, use agreement name</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <CmmPopupAnswersDialog
      v-model="showCmmPopupDialog"
      :questions="cmmPopupQuestionsFromConfig"
      :initial-answers="cmmPopupInitialAnswers"
      @confirm="onCmmPopupConfirm" />

    <ConfirmDialog
      v-model="showStartAutocompleteDialog"
      title="Start autocomplete?"
      :message="startAutocompleteMessage"
      confirm-text="Save & Start Autocomplete"
      cancel-text="Save Only"
      :loading="draftSavingInProgress"
      persistent
      @confirm="onStartAutocompleteConfirm"
      @cancel="onStartAutocompleteCancel" />
  </v-container>
</template>

<script setup>
import { ref, onMounted, onUnmounted, computed, nextTick, watch } from "vue";
import { storeToRefs } from "pinia";
import { useRouter, useRoute } from "vue-router";
import { mainServices } from "@/services/mainServices";
import { orderInfoService } from "@/services/orderInfoService";
import authService from "@/services/authService";
import { tryParseApiErrors, clearOrderInfoStorage } from "@/utils";
import { isValidCmmPortalSubmissionKey } from "@/utils/validators";
import { useOrderInfoStore } from "@/stores/orderInfoStore";
import { useAuthStore } from "@/stores";
import { useFieldChangeTracker } from "@/composables/useFieldChangeTracker";
import { CMM_REOPENABLE_STATUSES } from "@/models/paStatus";
import { useOrderInfoUpdates } from "@/composables/useOrderInfoUpdates";
import { useResponsive } from "@/composables/useResponsive";

const { isReadOnlyDevice } = useResponsive();
import PatientInformationFormPreview from "@/pages/PatientInformationFormPreview.vue";
import ProviderInformationFormPreview from "@/pages/ProviderInformationFormPreview.vue";
import MedicalInformationForm from "@/pages/MedicalInformationForm.vue";
import QuestionsSelectionComponent from "@/components/QuestionsSelectionComponent.vue";
import DiagnosisSelectionPreviewComponent from "@/components/DiagnosisSelectionPreviewComponent.vue";
import ExpeditedReviewComponent from "@/components/ExpeditedReviewComponent.vue";
import PatientNotificationComponent from "@/components/PatientNotificationComponent.vue";
import SignatureInputComponent from "@/components/SignatureInputComponent.vue";
import SupportingDocumentsPreviewComponent from "@/components/SupportingDocumentsPreviewComponent.vue";
import EditablePdfComponent from "@/components/EditablePdfComponent.vue";
import PortalFormSelection from "@/components/PortalFormSelection.vue";
import SubmissionPageSkeleton from "@/components/SubmissionPageSkeleton.vue";
import FlattenedPdfComponent from "@/components/FlattenedPdfComponent.vue";
import CmmPopupAnswersDialog from "@/components/CmmPopupAnswersDialog.vue";
import AppealLetterSection from "@/components/AppealLetterSection.vue";
import ConfirmDialog from "@/components/dashboard/ConfirmDialog.vue";
// import PharmacyLookupComponent from "@/components/PharmacyLookupComponent";

const props = defineProps({
  disabled: {
    type: Boolean,
    default: false,
  },
  filledFormPdfBlob: {
    type: Blob,
    default: null,
  },
  archivable: {
    type: Boolean,
    default: false,
  },
  // Provider read-only view (LAM-5263): render the fields, hide every action button.
  actionsVisible: {
    type: Boolean,
    default: true,
  },
});

const emit = defineEmits(["sendToPrescriber", "sendToPlan", "appealSent", "archive", "unarchive"]);

const { orderInfo } = storeToRefs(useOrderInfoStore());

const isArchived = computed(() => orderInfo.value?.status === "Archived");

// CMM popup: priorAuth from API (under uiConfig, camelCased by API).
const priorAuthFromConfig = computed(() => clientConfig.value?.uiConfig?.priorAuth);
const cmmPopupQuestionsFromConfig = computed(() => {
  const list = priorAuthFromConfig.value?.cmmPopupQuestions;
  return list && Array.isArray(list) ? list : [];
});
const cmmPopupAnswersFromConfig = computed(() => {
  const a = priorAuthFromConfig.value?.cmmPopupAnswers;
  return a && typeof a === "object" ? a : {};
});
const cmmPopupAnswersFromSubmission = computed(() => {
  const a = orderInfo.value?.orderSubmission?.cmmPopupAnswers;
  return a && typeof a === "object" ? a : {};
});

const incompleteStatuses = ["Created", "Incomplete", "Review demographics info", "Requires review"];
const reviewStatuses = ["Review clinical info", "Requires review"];
const reopeningStatuses = CMM_REOPENABLE_STATUSES;
const isReopening = computed(() => {
  return reopeningStatuses.includes(orderInfo.value.status) && orderInfo.value.submissionMethod === "CoverMyMeds";
});
// One computed per editability rule, passed to both `is-editable` and `form-button-clicked` so a
// section can never ask for input it doesn't render (LAM-4993) — a CMM import lands here at status
// "Created" ("Fetching questions") with the whole form read-only, yet validation painted anyway
// because formButtonClicked starts true.
const orderFieldsEditable = computed(
  () => !props.disabled && (incompleteStatuses.includes(orderInfo.value.status) || isReopening.value)
);
const clinicalAnswersEditable = computed(
  () => !props.disabled && (reviewStatuses.includes(orderInfo.value.status) || isReopening.value)
);
const supportingDocsEditable = computed(() => orderFieldsEditable.value || clinicalAnswersEditable.value);
const delegateAccountSubmitRequired = computed(() => priorAuthFromConfig.value?.delegateAccountSubmitRequired === true);
const docsNotRequiredOptinEnabled = computed(() => priorAuthFromConfig.value?.docsNotRequiredOptinEnabled === true);
const buttonText = computed(() => {
  if (isReopening.value) return "Re-Open Submission";

  if (incompleteStatuses.includes(orderInfo.value.status) && orderInfo.value.status !== "Requires review") {
    return "Send to Processing";
  } else if (delegateAccountSubmitRequired.value && orderInfo.value.status === "Review clinical info") {
    return "Send to Plan from Delegate Account";
  } else {
    return "Send to Plan";
  }
});
const sendToPlanDisabledReason = "Provider does not have a delegate agreement. Use Send to Prescriber instead.";
const sendToPrescriberDisabledReason =
  "Cannot send to prescriber: CoverMyMeds submission key is missing or invalid. Please reimport the submission with a valid key.";
const isSendToPlanFromDelegateAction = computed(() => buttonText.value === "Send to Plan from Delegate Account");
const isSendToPlanDisabledByDelegateAgreement = computed(() => {
  if (!isSendToPlanFromDelegateAction.value || !delegateAccountSubmitRequired.value) {
    return false;
  }
  if (orderInfo.value?.providerDelegateAgreement === true) {
    return false;
  }
  if (orderInfo.value?.providerDelegateAgreementSuggestion) {
    return false;
  }
  return true;
});
const isSendToPrescriberDisabledByCmmKey = computed(() => {
  if (orderInfo.value?.submissionMethod !== "CoverMyMeds") {
    return false;
  }
  return !isValidCmmPortalSubmissionKey(orderInfo.value?.orderSubmission?.portalSubmissionId);
});

const { trackChange, isReopenEligible, requiresNewSubmission } = useFieldChangeTracker(orderInfo.value);

const {
  supplementalDocs,
  formButtonClicked,
  updateChecklistSelection,
  updateBasicPlusChecklistSelection,
  updateSignature,
  updatePrimaryDiagnosis,
  updateOtherDiagnosis,
  addOtherDiagnosis,
  addFreeFormSupplementalDocs,
  updateSupportingDocs,
  addExtraMedication,
  removeExtraMedication,
  removePrimaryMedication,
  updateExtraMedicationField,
  validPatientInfo,
  validProviderInfo,
  validMedicationInfo,
  validDiagnosisInfo,
  validSupportingDocs,
  mergeSupplementalDocsWithPatientChart,
  savePartialOrderInfo,
} = useOrderInfoUpdates(orderInfo);

const expandedSections = ref([]);
const showDelegateAgreementDialog = ref(false);
const pendingDelegateAgreementSuggestion = ref(null);
const showCmmPopupDialog = ref(false);
const clientConfig = ref(null);

const draftSavingInProgress = ref(false);
// Not tied to props.disabled — send-to-prescriber allowed across statuses (LAM-3090 resend).
const isSendToPrescriberDisabled = computed(
  () => draftSavingInProgress.value || isSendToPrescriberDisabledByCmmKey.value
);
const checklistLiveErrors = ref({});
const basicPlusLiveErrors = ref({});

function onLiveValidationErrors(errorsMap, section) {
  if (section === "checklist") {
    checklistLiveErrors.value = errorsMap;
  } else {
    basicPlusLiveErrors.value = errorsMap;
  }
  // Persist cleared errors into orderInfo so the next saveOrder call writes null to the DB.
  // Without this, checklistLlmScores.validationError survives a page reload even after the
  // user has corrected the value, because _set_fields blindly re-saves whatever the frontend sent.
  const scores =
    section === "checklist" ? orderInfo.value.checklistLlmScores : orderInfo.value.basicPlusChecklistLlmScores;
  if (!scores) return;
  Object.entries(errorsMap).forEach(([answerId, error]) => {
    if (error === null) {
      const score = scores.find((s) => s.answerId === answerId);
      if (score) score.validationError = null;
    }
  });
}
const checklistHasValidationErrors = computed(() => {
  const allQuestions = [...(orderInfo.value.payerQuestions || []), ...(orderInfo.value.basicPlusQuestions || [])];
  const scores = [
    ...(orderInfo.value.checklistLlmScores || []),
    ...(orderInfo.value.basicPlusChecklistLlmScores || []),
  ];
  const combinedLiveErrors = { ...checklistLiveErrors.value, ...basicPlusLiveErrors.value };
  // ".+" means "not empty" — that's enforced by the required-field validation on plan submit,
  // not a format rule. Don't block send-to-prescriber for empty-check violations.
  // A Pinia-flagged field is skipped when the user has live-revalidated it to null (fixed).
  const hasFormatError = scores.some((s) => {
    if (!s.validationError) return false;
    const q = allQuestions.find((q) => q.requirementRuleName === s.answerId);
    if (q && q.validationRule === ".+") return false;
    return !(s.answerId in combinedLiveErrors && combinedLiveErrors[s.answerId] === null);
  });
  const hasLiveError = Object.values(combinedLiveErrors).some(Boolean);
  return hasFormatError || hasLiveError;
});
const submitButtonDisabled = computed(() => {
  return (
    draftSavingInProgress.value ||
    props.disabled ||
    (isReopening.value && !isReopenEligible.value) ||
    isSendToPlanDisabledByDelegateAgreement.value
  );
});

function onClearPolicyReview(questionId) {
  const review = (orderInfo.value.checklistPolicyReviews || []).find((r) => r.questionId === questionId);
  if (review && review.alignsWithPolicy === "NO") {
    review.alignsWithPolicy = "YES";
    delete review.reason;
    delete review.policyCitation;
    delete review.policyDocumentUrl;
  }
}

function formatProviderDisplayName(firstName, lastName) {
  const parts = [firstName, lastName].filter(Boolean);
  if (!parts.length) {
    return "Unknown provider";
  }
  return parts.map((part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase()).join(" ");
}

const enteredProviderDisplayName = computed(() =>
  formatProviderDisplayName(orderInfo.value?.providerFirstName, orderInfo.value?.providerLastName)
);

const suggestedProviderDisplayName = computed(() => {
  const suggestion = pendingDelegateAgreementSuggestion.value || orderInfo.value?.providerDelegateAgreementSuggestion;
  if (!suggestion) {
    return "";
  }
  return formatProviderDisplayName(suggestion.providerFirstName, suggestion.providerLastName);
});

async function resolveDelegateAgreementCheck() {
  const providerFields = {
    providerFirstName: orderInfo.value?.providerFirstName,
    providerLastName: orderInfo.value?.providerLastName,
    providerNpiNumber: orderInfo.value?.providerNpiNumber,
  };
  if (orderInfo.value?.orderKey) {
    return orderInfoService.checkProviderDelegateAgreement(orderInfo.value.orderKey, providerFields);
  }
  return {
    hasAgreement: orderInfo.value?.providerDelegateAgreement === true,
    suggestion: orderInfo.value?.providerDelegateAgreementSuggestion || null,
  };
}

async function maybePromptDelegateAgreementConfirmation() {
  if (!isSendToPlanFromDelegateAction.value || !delegateAccountSubmitRequired.value) {
    return false;
  }
  try {
    const check = await resolveDelegateAgreementCheck();
    if (check.hasAgreement) {
      orderInfo.value.providerDelegateAgreement = true;
      orderInfo.value.providerDelegateAgreementSuggestion = null;
      return false;
    }
    if (check.suggestion) {
      pendingDelegateAgreementSuggestion.value = check.suggestion;
      showDelegateAgreementDialog.value = true;
      return true;
    }
    return false;
  } catch (err) {
    errors.value = tryParseApiErrors(err);
    return false;
  }
}

// Either representation changing is a change worth saving. Comparing the geometry too matters for
// the case that motivates this whole feature: a user whose stored signature is a legacy PNG re-draws
// it, and the new image can differ only slightly while the geometry goes from absent to present —
// which is the difference between a fax the payor can read and one they can't.
function signatureChanged() {
  if (userSignature.value !== initialUserSignature.value) {
    return true;
  }
  const current = JSON.stringify(userSignatureStrokes.value ?? null);
  const initial = JSON.stringify(initialUserSignatureStrokes.value ?? null);
  return current !== initial;
}

/**
 * Write the drawn signature to the user's profile if it changed. Returns false if the save failed.
 *
 * The pad edits refs; only this persists them, and the fax overlay reads `User.signature` /
 * `signature_strokes` back from the DB at send time — so anything that renders or sends a fax has
 * to have called this first, or it stamps the previous signature. That is why it runs from the
 * draft save as well as from submit: when a fax is refused for a signature that can't survive
 * transmission, the user re-draws in the pad and goes straight back to Preview or Send, neither of
 * which submits the order. Without this the re-draw would never leave the browser and the refusal
 * would repeat forever.
 *
 * Deliberately not gated on `userSignature.value` being truthy — clicking Clear sets it to null,
 * and that has to persist too, or a signature the user explicitly removed stays live on their
 * profile and goes out on the next fax anyway.
 */
async function persistSignatureIfChanged() {
  if (!electronicSignatureEnabled.value || !signatureChanged()) {
    return true;
  }
  try {
    await authService.updateSignature(userSignature.value, userSignatureStrokes.value);
    // Only now is the saved state the new one — otherwise every later draft save re-sends an
    // unchanged signature, and a failed save would be forgotten rather than retried.
    initialUserSignature.value = userSignature.value;
    initialUserSignatureStrokes.value = userSignatureStrokes.value;
    return true;
  } catch (err) {
    errors.value = tryParseApiErrors(err);
    return false;
  }
}

async function continueSubmitAfterDelegateChecks() {
  const isValid = await validateForm();
  if (!isValid) {
    return;
  }
  // Must happen here, not inside performSubmission() — for eFax orders (the only submission
  // method this feature applies to) this function returns right after emitting "sendToPlan"
  // below, and performSubmission() is never called at all.
  // Must not swallow a save failure: a stale or missing signature would be attached to the fax.
  if (!(await persistSignatureIfChanged())) {
    return;
  }
  emit("sendToPlan");
  if (orderInfo.value?.submissionMethod === "eFax") {
    return;
  }
  if (needCmmPopupAnswersFromUser.value) {
    showCmmPopupDialog.value = true;
    return;
  }
  await performSubmission();
}

async function confirmDelegateAgreementMatch() {
  const suggestion = pendingDelegateAgreementSuggestion.value;
  if (!suggestion) {
    showDelegateAgreementDialog.value = false;
    return;
  }
  orderInfo.value.providerFirstName = suggestion.providerFirstName;
  orderInfo.value.providerLastName = suggestion.providerLastName;
  orderInfo.value.providerNpiNumber = suggestion.providerNpiNumber;
  trackChange("providerFirstName", suggestion.providerFirstName);
  trackChange("providerLastName", suggestion.providerLastName);
  trackChange("providerNpiNumber", suggestion.providerNpiNumber);
  orderInfo.value.providerDelegateAgreement = true;
  orderInfo.value.providerDelegateAgreementSuggestion = null;
  pendingDelegateAgreementSuggestion.value = null;
  showDelegateAgreementDialog.value = false;
  await continueSubmitAfterDelegateChecks();
}

const hasConfigCmmPopupAnswers = computed(() => Object.keys(cmmPopupAnswersFromConfig.value).length > 0);
const hasSubmissionCmmPopupAnswers = computed(() => Object.keys(cmmPopupAnswersFromSubmission.value).length > 0);
const needCmmPopupAnswersFromUser = computed(() => {
  if (orderInfo.value?.submissionMethod !== "CoverMyMeds") return false;
  if (!reviewStatuses.includes(orderInfo.value?.status)) return false;
  if (hasSubmissionCmmPopupAnswers.value) return false;
  if (hasConfigCmmPopupAnswers.value) return false;
  return true;
});
const cmmPopupInitialAnswers = computed(() => {
  const submission = cmmPopupAnswersFromSubmission.value;
  const config = cmmPopupAnswersFromConfig.value;
  return { ...config, ...submission };
});

const errors = ref([]);

const router = useRouter();
const preloader = ref(false);
const editablePdf = ref(null);
const payerQuestionsTitle = "Insurance Coverage Criteria";
const basicPlusQuestionsTitle = "Additional Questions";

const appealLettersEnabled = computed(() => priorAuthFromConfig.value?.appealLettersEnabled ?? false);
const electronicSignatureEnabled = computed(() => priorAuthFromConfig.value?.electronicSignatureEnabled === true);
const userSignature = ref(null);
const initialUserSignature = ref(null);
// Stroke geometry for the same signature. Kept as its own ref rather than derived from the image
// because it is null whenever the pad can't capture faithfully (a stored signature loaded as
// pixels), and that distinction is exactly what decides raster vs vector rendering on the fax.
const userSignatureStrokes = ref(null);
const initialUserSignatureStrokes = ref(null);
const isDenied = computed(() => orderInfo.value?.status === "Denied");
const showAppeal = computed(() => isDenied.value && appealLettersEnabled.value);
const appealRoute = useRoute();

watch(
  showAppeal,
  (show) => {
    if (!show) return;
    if (!expandedSections.value.includes("appeal")) expandedSections.value.push("appeal");
    if (appealRoute.query.section === "appeal") {
      nextTick(() =>
        document.getElementById("draft-appeal-section")?.scrollIntoView({ behavior: "smooth", block: "start" })
      );
    }
  },
  { immediate: true }
);

onMounted(async () => {
  orderInfo.value.formStep = "Review";
  supplementalDocs.value.push({ label: null, links: [] });
  setDefaultExpandedSections();
  // A company-less provider login (LAM-5263) has no client config; skip the fetch (it 400s).
  if (!useAuthStore().userInfo?.isProvider) {
    try {
      clientConfig.value = await mainServices.getCurrentUserClientConfig();
    } catch {
      // Suppress - CMM popup will show when required if config fails to load
    }
  }
  if (electronicSignatureEnabled.value) {
    try {
      const { signature, signatureStrokes } = await authService.getSignature();
      userSignature.value = signature;
      initialUserSignature.value = signature;
      userSignatureStrokes.value = signatureStrokes ?? null;
      initialUserSignatureStrokes.value = userSignatureStrokes.value;
    } catch {
      // Suppress - signature pad just starts blank if it fails to load
    }
  }
});

onUnmounted(() => {
  expandedSections.value = [];
  clearOrderInfoStorage();
});

function updateOrderInfoField(field, value) {
  orderInfo.value[field] = value;
  trackChange(field, value);
}

// Every v-expansion-panel value in this template. Values for panels that aren't rendered are
// harmless to include, so keep this exhaustive — a missing one silently leaves that panel collapsed
// for read-only viewers (that's how portal-forms was missed).
const ALL_EXPANDABLE_SECTIONS = [
  "appeal",
  "portal-forms",
  "patient-info",
  "provider-info",
  "medical-info",
  "diagnosis-info",
  "patient-notification",
  "additional-questions",
  "insurance-coverage",
  "editable-pdf",
  "supporting-docs",
  "expedited-review",
  "signature",
];

function setDefaultExpandedSections() {
  // Read-only viewers (provider logins, LAM-5263) can't act on the page, so nothing is gained by
  // making them open panels by hand — and terminal statuses (Approved/Denied) pre-expand nothing.
  if (!props.actionsVisible) {
    expandedSections.value = [...ALL_EXPANDABLE_SECTIONS];
    return;
  }
  if (incompleteStatuses.includes(orderInfo.value.status)) {
    expandedSections.value = [
      "patient-info",
      "provider-info",
      "medical-info",
      "diagnosis-info",
      "patient-notification",
      "supporting-docs",
      "expedited-review",
      "signature",
    ];

    if (orderInfo.value.basicPlusChecklist.length > 0) {
      expandedSections.value.push("additional-questions");
    }
    if (orderInfo.value.payerQuestions.length > 0 && !orderInfo.value.editableSubmissionForm) {
      expandedSections.value.push("insurance-coverage");
    }
    if (orderInfo.value.editableSubmissionForm) {
      expandedSections.value.push("editable-pdf");
    }
  }

  if (reviewStatuses.includes(orderInfo.value.status)) {
    expandedSections.value = ["insurance-coverage", "editable-pdf", "signature"];
  }
}

async function handleSavePartialOrderInfo() {
  if (shouldOfferStartAutocomplete.value) {
    showStartAutocompleteDialog.value = true;
    return;
  }
  await saveDraftNow();
}

async function saveDraftNow({ startAutocomplete = false } = {}) {
  draftSavingInProgress.value = true;
  if (orderInfo.value.editableSubmissionForm) {
    if (!expandedSections.value.includes("editable-pdf")) {
      expandedSections.value.push("editable-pdf");
    }
    if (!editablePdf.value) {
      // timeout to ensure the component is mounted before validation
      await new Promise((resolve) => setTimeout(resolve, 0));
    }
    if (editablePdf.value) {
      editablePdf.value.mergeOverlayToPdfForm();
    }
  }
  try {
    errors.value = [];
    // Before the order, and not conditional on it: the fax editor's Preview and Send both route
    // here (via onPreSend) and neither submits the order, so this is the only point at which a
    // re-drawn signature reaches the server ahead of the fax being built. A failure here leaves its
    // own message in `errors` and still lets the draft save, so the user's other edits aren't lost
    // to a signature problem.
    await persistSignatureIfChanged();
    await savePartialOrderInfo({ startAutocomplete });
  } catch (error) {
    errors.value = ["Failed to save draft. Please try again."];
  }
  draftSavingInProgress.value = false;
}

const showStartAutocompleteDialog = ref(false);
const autocompleteStartRequested = ref(false);

const supportingDocsCount = computed(() => {
  const chartDocs = (orderInfo.value?.patientChart || []).filter((doc) => doc?.links?.length > 0).length;
  const pendingDocs = (supplementalDocs.value || []).filter((doc) => doc?.links?.length > 0).length;
  return chartDocs + pendingDocs;
});

const shouldOfferStartAutocomplete = computed(
  () => orderInfo.value?.status === "Incomplete" && supportingDocsCount.value > 0 && !autocompleteStartRequested.value
);

const startAutocompleteMessage = computed(() => {
  const count = supportingDocsCount.value;
  const docs = count === 1 ? "1 supporting document" : `${count} supporting documents`;
  return `This submission is incomplete. Start autocomplete to draft answers to its clinical questions from your ${docs}?`;
});

async function onStartAutocompleteConfirm() {
  try {
    await saveDraftNow({ startAutocomplete: true });
    if (errors.value.length === 0) {
      autocompleteStartRequested.value = true;
    }
  } finally {
    showStartAutocompleteDialog.value = false;
  }
}

async function onStartAutocompleteCancel() {
  await saveDraftNow();
}

// Open the Signature panel from outside this component. Needed because the send-fax flow lives in the
// parent page: when the backend refuses a fax for an un-re-drawn signature, the pad the user has to
// act on is in here, inside a lazily-rendered expansion panel that may well be collapsed.
function focusSignatureSection() {
  if (!expandedSections.value.includes("signature")) {
    expandedSections.value.push("signature");
  }
}

defineExpose({
  saveDraft: saveDraftNow,
  performSubmission,
  focusSignatureSection,
});

async function validateForm() {
  formButtonClicked.value = true;
  if (supplementalDocs.value.length > 0 && supplementalDocs.value[0].links.length > 0) {
    mergeSupplementalDocsWithPatientChart();
  }
  errors.value = [];
  if (!validPatientInfo() && incompleteStatuses.includes(orderInfo.value.status)) {
    expandedSections.value.push("patient-info");
    errors.value.push("Please fill in all required patient information fields.");
  }
  if (!validProviderInfo() && incompleteStatuses.includes(orderInfo.value.status)) {
    expandedSections.value.push("provider-info");
    errors.value.push("Please fill in all required provider information fields.");
  }
  if (!validMedicationInfo() && incompleteStatuses.includes(orderInfo.value.status)) {
    expandedSections.value.push("medical-info");
    errors.value.push("Please fill in all required medication information fields.");
  }
  // The patientChartNotNeededOptIn clause reaches statuses where the diagnosis section is read-only
  // (the opt-in can only be ticked earlier, while it is editable), so gate it — otherwise submit is
  // blocked by a diagnosis the user has no field to enter.
  if (
    orderFieldsEditable.value &&
    !validDiagnosisInfo() &&
    (incompleteStatuses.includes(orderInfo.value.status) || orderInfo.value.patientChartNotNeededOptIn)
  ) {
    expandedSections.value.push("diagnosis-info");
    errors.value.push("Please fill in all required diagnosis information fields.");
  }
  if (supportingDocsEditable.value && !validSupportingDocs(!orderInfo.value.patientChartNotNeededOptIn)) {
    expandedSections.value.push("supporting-docs");
    errors.value.push("Please add at least one supporting document.");
  }
  if (orderInfo.value.editableSubmissionForm) {
    if (!expandedSections.value.includes("editable-pdf")) {
      expandedSections.value.push("editable-pdf");
    }
    if (!editablePdf.value) {
      // timeout to ensure the component is mounted before validation
      await new Promise((resolve) => setTimeout(resolve, 0));
    }
    if (editablePdf.value) {
      editablePdf.value.mergeOverlayToPdfForm();
      const isValid = editablePdf.value.validateAll();
      if (!isValid) {
        errors.value.push("Please fill in all required fields in the PDF form.");
      }
    }
  }
  expandedSections.value.push("insurance-coverage");
  expandedSections.value.push("additional-questions");
  await new Promise((resolve) => setTimeout(resolve, 300));
  const errorClassNames = ["v-input--error", "form-validation-alert"];
  let missingInput = [];
  errorClassNames.forEach((className) => {
    const foundElements = document.getElementsByClassName(className);
    missingInput = [...missingInput, ...foundElements];
  });
  if (missingInput.length === 0 && errors.value.length === 0) {
    return true;
  } else {
    await nextTick();
    requestAnimationFrame(() => {
      if (missingInput.length > 0) {
        missingInput[0].scrollIntoView({ behavior: "smooth", block: "start" });
      } else {
        const firstSection = expandedSections.value[0];
        const sectionEl = firstSection && document.querySelector(`[data-section="${firstSection}"]`);
        sectionEl?.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    });
    return false;
  }
}

async function submitOrderInfo() {
  if (isSendToPlanDisabledByDelegateAgreement.value) {
    return;
  }
  if (await maybePromptDelegateAgreementConfirmation()) {
    return;
  }
  await continueSubmitAfterDelegateChecks();
}

function onCmmPopupConfirm(answers) {
  orderInfo.value.cmmPopupAnswers = answers;
  performSubmission();
}

async function performSubmission() {
  draftSavingInProgress.value = true;
  try {
    if (isReopening.value) {
      if (!isReopenEligible.value) {
        draftSavingInProgress.value = false;
        return;
      }
      // Include field change tracking data for backend processing
      const submissionData = {
        ...orderInfo.value,
        requiresNewSubmission: requiresNewSubmission.value,
      };
      await orderInfoService.reopenSubmission(orderInfo.value.orderKey, submissionData);
      errors.value = [];
      expandedSections.value = [];
      clearOrderInfoStorage();
      router.push({ name: "all-submissions" });
      window.scrollTo(0, 0);
    } else {
      orderInfo.value.formStep = "Send to Plan";
      if (!orderInfo.value.orderKey) {
        const newOrder = await orderInfoService.createEmptyOrder(orderInfo.value);
        orderInfo.value.orderKey = newOrder.orderKey;
      }
      if (orderInfo.value.editableSubmissionForm) {
        if (!expandedSections.value.includes("editable-pdf")) {
          expandedSections.value.push("editable-pdf");
        }
        if (!editablePdf.value) {
          // timeout to ensure the component is mounted before validation
          await new Promise((resolve) => setTimeout(resolve, 0));
        }
        if (editablePdf.value) {
          editablePdf.value.mergeOverlayToPdfForm();
        }
      }
      await orderInfoService.saveOrder(orderInfo.value.orderKey, orderInfo.value);
      errors.value = [];
      expandedSections.value = [];
      clearOrderInfoStorage();
      router.push({ name: "all-submissions" });
      window.scrollTo(0, 0);
    }
  } catch (err) {
    errors.value = tryParseApiErrors(err);
  }
  draftSavingInProgress.value = false;
}

async function changeFormSelection(selectedFormId) {
  await new Promise((resolve) => setTimeout(resolve, 0));
  preloader.value = true;
  try {
    if (supplementalDocs.value.length > 0 && supplementalDocs.value[0].links.length > 0) {
      mergeSupplementalDocsWithPatientChart();
    }
    let data = orderInfo.value;
    data.portalFormId = selectedFormId;
    await orderInfoService.changeFormSelection(orderInfo.value.orderKey, data);
    errors.value = [];
    expandedSections.value = [];
    clearOrderInfoStorage();
    router.push({ name: "all-submissions" });
    window.scrollTo(0, 0);
  } catch (err) {
    errors.value = tryParseApiErrors(err);
  }
  preloader.value = false;
}

async function changeFormulary() {
  await new Promise((resolve) => setTimeout(resolve, 0));
  preloader.value = true;
  try {
    if (supplementalDocs.value.length > 0 && supplementalDocs.value[0].links.length > 0) {
      mergeSupplementalDocsWithPatientChart();
    }
    await orderInfoService.changeOrderFormulary(orderInfo.value.orderKey, orderInfo.value);
    errors.value = [];
    expandedSections.value = [];
    clearOrderInfoStorage();
    router.push({ name: "all-submissions" });
    window.scrollTo(0, 0);
  } catch (err) {
    errors.value = tryParseApiErrors(err);
  }
  preloader.value = false;
}

async function sendToPrescriber() {
  if (isSendToPrescriberDisabled.value) {
    return;
  }
  if (checklistHasValidationErrors.value) {
    errors.value = ["Please fix all answer format errors before sending to prescriber."];
    if (!expandedSections.value.includes("insurance-coverage")) expandedSections.value.push("insurance-coverage");
    if (!expandedSections.value.includes("additional-questions")) expandedSections.value.push("additional-questions");
    await new Promise((resolve) => setTimeout(resolve, 300));
    await nextTick();
    requestAnimationFrame(() => {
      const errorEl = document.querySelector(".v-input--error");
      if (errorEl) errorEl.scrollIntoView({ behavior: "smooth", block: "center" });
    });
    return;
  }
  if (!orderInfo.value.orderKey) {
    // No order yet — nothing to persist; let the parent's handler take over.
    emit("sendToPrescriber");
    return;
  }
  draftSavingInProgress.value = true;
  try {
    errors.value = [];
    // Fold any newly-added free-form supporting docs into patientChart before saving,
    // matching what validateForm() does for the Send-to-Plan path.
    if (supplementalDocs.value.length > 0 && supplementalDocs.value[0].links.length > 0) {
      mergeSupplementalDocsWithPatientChart();
    }
    // Persist the current in-memory orderInfo (including cleared/changed payer-question
    // answers in `checklist`) so the backend SYNC_ANSWERS_TO_CMM pipeline reads fresh
    // data. form_step="Review" hits PartialOrderSaveView's REVIEW branch which does a
    // flat _save_full_form (no status transition, no pipeline kick-off), unlike
    // "Send to Plan". See performSubmission() for the sibling pattern.
    orderInfo.value.formStep = "Review";
    await orderInfoService.saveOrder(orderInfo.value.orderKey, orderInfo.value);
    emit("sendToPrescriber");
  } catch (err) {
    errors.value = tryParseApiErrors(err);
  } finally {
    draftSavingInProgress.value = false;
  }
}
</script>
